import noop from 'lodash/noop'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button } from '@/components/button'
import CloseButton from '@/components/close-button'
import Icon from '@/components/icon'
import { createModal, useModalize } from '@/components/modalize/mui'
import { ParagraphRegular } from '@/components/text'
import { breakpoints, colors, fontWeights } from '@/styles'

const Title = styled.h1`
  color: ${colors.regular};
  text-align: center;
  font-weight: ${fontWeights.bold};
  font-size: 26px;
  letter-spacing: -0.2px;
  line-height: 36px;
  margin: 28px 0 8px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 24px;
    letter-spacing: -0.4px;
    line-height: 32px;
  }
`

const Description = styled(ParagraphRegular)`
  color: ${colors.foregroundSecondary};
  text-align: center;
  margin: 0 0 32px;
  max-width: 360px;
  button,
  a {
    color: ${colors.link};
    background: none;
    border: none;
    font-size: inherit;
    padding: 0;
    margin: 0;
    cursor: pointer;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`

const Container = styled.div`
  padding: 32px 32px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

type MessageModalProps = {
  title: React.ReactNode
  message?: React.ReactNode
  buttonText?: React.ReactNode
  buttonProps?: React.ComponentProps<typeof Button>
  onClose?: () => void
  onButtonClick?: () => void
  icon?: string
  children?: React.ReactNode
}

const MessageModal: React.FC<MessageModalProps> = ({
  title,
  message,
  buttonText,
  onButtonClick,
  buttonProps = { theme: 'primary' },
  icon = 'round_check',
  children,
  onClose = noop
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()

  const handleClose = () => {
    close()
    onClose()
  }

  const handleButtonClick = () => {
    close()
    onButtonClick ? onButtonClick() : onClose()
  }

  return (
    <Container>
      <CloseButton onClick={handleClose} />
      <Icon id={icon} width={75} height={75} />
      <Title>{title}</Title>
      <Description>{message}</Description>
      {children}
      <ButtonContainer>
        <Button onClick={handleButtonClick} {...buttonProps}>
          {buttonText || t('b.components.success_modal.ok.button', 'OK')}
        </Button>
      </ButtonContainer>
    </Container>
  )
}

const show = createModal(MessageModal, { clickOutside: false, showClose: false, width: '496px' })
export default show
export { MessageModal }
